import { types } from '../../actions/backendAsr';
import { types as callTypes } from '../../actions/backendCall';
import {
    getDefaultMicResults,
    onCallEnded,
    updateMicResults,
    updatePhraseSpottingResults,
    updateVoiceMetrics
} from '../../../utils/background/Utils';
import { normalizeSpottingConfig, prepareBlackList, prepareWhiteList, updateTranscript } from '../../../utils/background/asr';
import type {
    AsrConfigErrors,
    AsrMicResults,
    AsrMicResultHistoryItem,
    AsrRawResults,
    AsrRecognitionConfig,
    AsrSpottingConfig,
    AsrSpottingPhrase,
    AsrVoiceMetrics
} from '../../types';
import type { TranscriptItem } from '../../../utils/transcript';

interface AsrState {
    config: {
        languageCode: string | null;
        lingwareTopic: string | null;
        spottingConfig: AsrSpottingConfig | null;
        asr: AsrRecognitionConfig | null;
    };
    micResults: AsrMicResults;
    micResultsRaw: AsrRawResults | null;
    micResultsHistory: AsrMicResultHistoryItem[];
    micVoiceMetrics: AsrVoiceMetrics;
    speakerVoiceMetrics: AsrVoiceMetrics;
    timelineTranscripts: TranscriptItem[] | null;
    micSessionNumber: number;
    speakerSessionNumber: number;
    asrConfigErrors: AsrConfigErrors;
}

export const initialState: AsrState = {
    config: {
        languageCode: null,
        lingwareTopic: null,
        spottingConfig: null,
        asr: null
    },
    micResults: {
        speechRate: null,
        whitelist: [],
        blacklist: [],
        language: null
    },
    micResultsRaw: null,
    micResultsHistory: [],
    micVoiceMetrics: {},
    speakerVoiceMetrics: {},
    timelineTranscripts: null,
    micSessionNumber: 0,
    speakerSessionNumber: 0,
    asrConfigErrors: {}
};

interface AsrAction {
    type?: string;
    payload?: Record<string, unknown> | null;
}

interface AsrConfigPayload {
    languageCode?: string;
    lingwareTopic?: string;
    spottingConfig?: AsrSpottingConfig | null;
    asr?: AsrRecognitionConfig | null;
    errors?: AsrConfigErrors;
}

function toAsrConfigPayload(payload: Record<string, unknown>): AsrConfigPayload {
    return {
        languageCode: typeof payload.languageCode === 'string' ? payload.languageCode : undefined,
        lingwareTopic: typeof payload.lingwareTopic === 'string' ? payload.lingwareTopic : undefined,
        spottingConfig: toSpottingConfig(payload.spottingConfig),
        asr: isRecognitionConfig(payload.asr) ? payload.asr : null,
        errors: toAsrConfigErrors(payload.errors)
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toSpottingPhrase(value: unknown): AsrSpottingPhrase | null {
    if (!isRecord(value)) {
        return null;
    }

    const head = Array.isArray(value.head) ? value.head.filter((item): item is string => typeof item === 'string') : undefined;
    const complement = Array.isArray(value.complement)
        ? value.complement.filter((item): item is string => typeof item === 'string')
        : undefined;

    return {
        groupName: typeof value.groupName === 'string' ? value.groupName : undefined,
        head,
        complement,
        kind: typeof value.kind === 'string' || typeof value.kind === 'number' ? value.kind : undefined,
        points: typeof value.points === 'number' ? value.points : undefined,
        visible: typeof value.visible === 'boolean' ? value.visible : undefined
    };
}

function toSpottingConfig(value: unknown): AsrSpottingConfig | null {
    if (!isRecord(value)) {
        return null;
    }

    const whitelist = Array.isArray(value.whitelist) ? (value.whitelist.map(toSpottingPhrase).filter(Boolean) as AsrSpottingPhrase[]) : [];
    const blacklist = Array.isArray(value.blacklist) ? (value.blacklist.map(toSpottingPhrase).filter(Boolean) as AsrSpottingPhrase[]) : [];

    return { whitelist, blacklist };
}

function isRecognitionConfig(value: unknown): value is AsrRecognitionConfig {
    if (!isRecord(value)) {
        return false;
    }

    const hasLanguageCode = typeof value.languageCode === 'string';
    const hasVendors = value.vendors === null || isRecord(value.vendors);
    const hasSpottingConfig = value.spottingConfig === undefined || value.spottingConfig === null || isRecord(value.spottingConfig);

    return hasLanguageCode || hasVendors || hasSpottingConfig;
}

function toAsrConfigErrors(value: unknown): AsrConfigErrors | undefined {
    if (!isRecord(value)) {
        return undefined;
    }

    return {
        noWhitelist: typeof value.noWhitelist === 'boolean' ? value.noWhitelist : undefined,
        noBlacklist: typeof value.noBlacklist === 'boolean' ? value.noBlacklist : undefined,
        noTeam: typeof value.noTeam === 'boolean' ? value.noTeam : undefined
    };
}

export default function asrReducer(state = initialState, action: AsrAction = {}): AsrState {
    const { type, payload } = action;
    const p = payload || {};
    switch (type) {
        case types.GET_USER_ASR_CONFIG_SUCCESS: {
            const configPayload = toAsrConfigPayload(p);
            const spottingConfig = normalizeSpottingConfig(configPayload.spottingConfig || { whitelist: [], blacklist: [] });

            return {
                ...state,
                config: {
                    languageCode: configPayload.languageCode || null,
                    lingwareTopic: configPayload.lingwareTopic || null,
                    spottingConfig,
                    asr: configPayload.asr || null
                },
                asrConfigErrors: configPayload.errors || {},
                micResults: getDefaultMicResults(spottingConfig)
            };
        }
        case types.UPDATE_USER_ASR_CONFIG_SUCCESS: {
            const configPayload = toAsrConfigPayload(p);
            return {
                ...state,
                asrConfigErrors: configPayload.errors || {},
                config: {
                    languageCode: configPayload.languageCode || null,
                    lingwareTopic: configPayload.lingwareTopic || null,
                    spottingConfig: normalizeSpottingConfig(configPayload.spottingConfig || { whitelist: [], blacklist: [] }),
                    asr: configPayload.asr || null
                }
            };
        }
        case types.GET_USER_ASR_CONFIG_FAIL:
        case types.UPDATE_USER_ASR_CONFIG_FAIL:
            return {
                ...state,
                config: {
                    languageCode: null,
                    lingwareTopic: null,
                    spottingConfig: null,
                    asr: null
                }
            };

        case types.MIC_UPDATE_ASR_RESULTS:
            return {
                ...state,
                ...updateMicResults(state, p),
                timelineTranscripts: updateTranscript(state.timelineTranscripts || [], 'micResults', p)
            };
        case types.MIC_UPDATE_PHRASE_SPOTTING_RESULTS:
            return {
                ...state,
                ...updatePhraseSpottingResults(state, p)
            };
        case types.SPEAKER_UPDATE_ASR_RESULTS:
            return {
                ...state,
                timelineTranscripts: updateTranscript(state.timelineTranscripts || [], 'speakerResults', p)
            };

        case types.MIC_UPDATE_ASR_VOICE_METRICS:
            return {
                ...state,
                micVoiceMetrics: updateVoiceMetrics(state.micVoiceMetrics, p)
            };
        case types.SPEAKER_UPDATE_ASR_VOICE_METRICS:
            return {
                ...state,
                speakerVoiceMetrics: updateVoiceMetrics(state.speakerVoiceMetrics, p)
            };

        case types.MIC_UPDATE_ASR_SPEECH_PACE:
            return {
                ...state,
                micVoiceMetrics: {
                    ...state.micVoiceMetrics,
                    speechPace: typeof p.speechPace === 'number' ? p.speechPace : null,
                    newSpeechRate: typeof p.wordsPerMinute === 'number' ? p.wordsPerMinute : null
                }
            };
        case types.CLEAR_HISTORY:
            return {
                ...state,
                micResultsHistory: []
            };

        case callTypes.CALL_STARTED:
            return {
                ...state,
                micResults: {
                    speechRate: null,
                    whitelist: prepareWhiteList(state.config.spottingConfig || { whitelist: [], blacklist: [] }),
                    blacklist: prepareBlackList(state.config.spottingConfig || { whitelist: [], blacklist: [] }),
                    language: state.config.languageCode
                },
                timelineTranscripts: [],
                micResultsRaw: null,
                micSessionNumber: state.micSessionNumber + 1,
                speakerSessionNumber: state.speakerSessionNumber + 1
            };
        case callTypes.CALL_ENDED:
            return {
                ...state,
                ...onCallEnded(state, p)
            };

        default:
            return state;
    }
}
